import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, CheckCircle } from '@phosphor-icons/react';
import { useNotifications } from '../../hooks/useNotifications';
import styles from './NotificationBell.module.css';

/**
 * NotificationBell - Bell icon with dropdown for notifications
 *
 * Features:
 * - Unread count badge
 * - Dropdown list of recent notifications
 * - Mark as read/dismiss actions
 * - Click to navigate to related entity
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const {
    recentNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    getNotificationStyle,
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle notification click
  const handleNotificationClick = notification => {
    // Mark as read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to related entity if available
    if (notification.entity_type && notification.entity_id) {
      switch (notification.entity_type) {
        case 'quote':
          navigate('/vendors');
          break;
        case 'payment':
          navigate('/budget');
          break;
        default:
          break;
      }
    }

    setIsOpen(false);
  };

  // Format relative time
  const formatRelativeTime = dateStr => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'עכשיו';
    if (diffMins < 60) return `לפני ${diffMins} דקות`;
    if (diffHours < 24) return `לפני ${diffHours} שעות`;
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    return date.toLocaleDateString('he-IL');
  };

  return (
    <div className={styles.container}>
      {/* Bell Button */}
      <button
        ref={buttonRef}
        className={styles.bellButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="התראות"
      >
        <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {/* Header */}
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>התראות</span>
            {unreadCount > 0 && (
              <button className={styles.markAllRead} onClick={markAllAsRead}>
                <CheckCircle size={16} />
                סמן הכל כנקרא
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className={styles.notificationsList}>
            {isLoading ? (
              <div className={styles.loadingState}>טוען...</div>
            ) : recentNotifications.length === 0 ? (
              <div className={styles.emptyState}>
                <Bell size={32} className={styles.emptyIcon} />
                <span>אין התראות חדשות</span>
              </div>
            ) : (
              recentNotifications.map(notification => {
                const style = getNotificationStyle(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div
                      className={styles.notificationDot}
                      style={{ backgroundColor: style.color }}
                    />
                    <div className={styles.notificationContent}>
                      <div className={styles.notificationTitle}>{notification.title}</div>
                      {notification.message && (
                        <div className={styles.notificationMessage}>{notification.message}</div>
                      )}
                      <div className={styles.notificationMeta}>
                        <span className={styles.notificationType} style={{ color: style.color }}>
                          {style.label}
                        </span>
                        <span className={styles.notificationTime}>
                          {formatRelativeTime(notification.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.notificationActions}>
                      {!notification.read && (
                        <button
                          className={styles.actionButton}
                          onClick={e => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          title="סמן כנקרא"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        className={styles.actionButton}
                        onClick={e => {
                          e.stopPropagation();
                          dismissNotification(notification.id);
                        }}
                        title="הסר"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
